import { t } from 'i18next';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon, IconName } from '../../../components/Icon';
import { Modal } from '../../../components/Modal';
import { usePrincipals } from '../../../hooks/queries';
import { getCurrentPrincipal } from '../../../lib/currentPrincipal';
import { trackSharepointDownload } from '../../../utils/principal-service/event-endpoints';
import { DocumentFile, DocumentItem } from './DocumentFile';
import { DocumentFolder } from './DocumentFolder';

export const mimeTypeIcons: Record<string, IconName> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'FileDoc',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'FileXLS',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'FilePPT',
  'application/pdf': 'FilePDF',
  'image/png': 'FileImage',
  'image/jpg': 'FileImage',
  'image/jpeg': 'FileImage',
};

interface SharedDocumentItemProps {
  item: DocumentItem;
  itemDeleteCallback: () => void;
}

export const SharedDocumentItem = ({
  item,
  itemDeleteCallback = () => null,
}: SharedDocumentItemProps) => {
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const handleDeleteButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevents the event from bubbling up to the Link
    e.preventDefault(); // prevents default link navigation behavior
    setShowDeleteModal(true);
  };

  const handleDeleteFile = () => {
    itemDeleteCallback();
    setShowDeleteModal(false);
  };
  const queryParams = new URLSearchParams();
  if (item instanceof DocumentFolder) {
    queryParams.set('drive', item.siteId ?? params.get('drive') ?? '');
    queryParams.set('path', `${params.get('path') ?? ''}/${item.name}`);
  }

  return (
    <>
      <Link
        to={
          item instanceof DocumentFile
            ? item.url ?? ''
            : {
                search: `?${queryParams?.toString()}`,
              }
        }
        target={item instanceof DocumentFile ? '_blank' : undefined}
        rel={item instanceof DocumentFile ? 'noopener noreferrer' : undefined}
        onClick={() => {
          item instanceof DocumentFile
            ? trackSharepointDownload({
                principalId: currentPrincipal.id,
                fileName: item.name ?? '',
              })
            : null;
        }}
        className="border-grey-300 hover:bg-grey-100 grid w-full cursor-pointer grid-cols-12 border-b py-4"
      >
        <div className="xs:col-span-7 flex w-full items-center">
          <div className="w-4 shrink-0">
            <Icon
              name={
                item instanceof DocumentFile
                  ? mimeTypeIcons[item.mimeType ?? ''] ?? 'File'
                  : 'Folder'
              }
              inline={true}
            />
          </div>
          <div className="tooltip pl-2">{item.name}</div>
        </div>
        <div className="xs:col-span-2 w-full">
          {item.lastModified?.toLocaleDateString()}
        </div>
        <div className="xs:col-span-2 w-full">{item.owner}</div>
        {item instanceof DocumentFile ? (
          <div className="col-span-1 w-full">
            <button
              title="delete-file"
              className="ml-4 w-4"
              onClick={handleDeleteButtonClick}
            >
              <Icon
                name="Delete"
                inline={true}
                color="primary"
                hoverColor="red-100"
              />
            </button>
          </div>
        ) : null}
      </Link>
      <Modal
        heading={t('features:shared-documents:delete-modal:heading')}
        isHidden={!showDeleteModal}
        confirmText={t('features:shared-documents:delete-modal:heading')}
        onConfirm={handleDeleteFile}
        onCancel={() => setShowDeleteModal(false)}
      >
        {`${t('features:shared-documents:delete-modal:description')} ${
          item.name
        }?`}
      </Modal>
    </>
  );
};
